import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface TaskRecord {
  id: string;
  task_id: string;
  description: string;
  status: string;
  tokens_used: number;
  cost_usd: number;
  qa_score: number | null;
  qa_passed: boolean | null;
  files_generated: string[];
  created_at: string;
  completed_at: string;
  duration_ms: number | null;
  source: string;
}

const statusColors: Record<string, string> = {
  completed: '#22c55e',
  deployed: '#3b82f6',
  qa_failed: '#ef4444',
  subtask: '#a855f7',
  cleaned: '#f59e0b',
};

const formatDuration = (ms: number | null): string => {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const TaskHistoryPage: React.FC = () => {
  const [tasks, setTasks] = useState<TaskRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ total: 0, completed: 0, deployed: 0, failed: 0, tokens: 0, cost: 0 });

  useEffect(() => {
    loadTasks();
    loadStats();
  }, [filter]);

  const loadTasks = async () => {
    setLoading(true);
    let query = supabase
      .from('task_history')
      .select('*')
      .order('completed_at', { ascending: false })
      .limit(200);

    if (filter !== 'all') {
      query = query.eq('status', filter);
    }

    const { data, error } = await query;
    if (error) {
      console.error('Error loading tasks:', error);
    } else {
      setTasks(data || []);
    }
    setLoading(false);
  };

  const loadStats = async () => {
    const { data, error } = await supabase
      .from('task_history')
      .select('status, tokens_used, cost_usd');

    if (!error && data) {
      const stats = {
        total: data.length,
        completed: data.filter(t => t.status === 'completed').length,
        deployed: data.filter(t => t.status === 'deployed').length,
        failed: data.filter(t => t.status === 'qa_failed').length,
        tokens: data.reduce((sum, t) => sum + (t.tokens_used || 0), 0),
        cost: data.reduce((sum, t) => sum + (t.cost_usd || 0), 0),
      };
      setStats(stats);
    }
  };

  const filteredTasks = tasks.filter(task =>
    !search || task.description?.toLowerCase().includes(search.toLowerCase()) ||
    task.task_id?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <main className="flex-1 p-6 overflow-y-auto">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-cyan-400 mb-2">Task History</h1>
          <p className="text-gray-400">All {stats.total} tasks from Supabase database</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-[#1a1a2e] rounded-lg p-4 border border-gray-700">
            <div className="text-2xl font-bold text-white">{stats.total}</div>
            <div className="text-xs text-gray-400">Total Tasks</div>
          </div>
          <div className="bg-[#1a1a2e] rounded-lg p-4 border border-green-900">
            <div className="text-2xl font-bold text-green-400">{stats.completed}</div>
            <div className="text-xs text-gray-400">Completed</div>
          </div>
          <div className="bg-[#1a1a2e] rounded-lg p-4 border border-blue-900">
            <div className="text-2xl font-bold text-blue-400">{stats.deployed}</div>
            <div className="text-xs text-gray-400">Deployed</div>
          </div>
          <div className="bg-[#1a1a2e] rounded-lg p-4 border border-red-900">
            <div className="text-2xl font-bold text-red-400">{stats.failed}</div>
            <div className="text-xs text-gray-400">QA Failed</div>
          </div>
          <div className="bg-[#1a1a2e] rounded-lg p-4 border border-purple-900">
            <div className="text-2xl font-bold text-purple-400">{(stats.tokens / 1000).toFixed(0)}k</div>
            <div className="text-xs text-gray-400">Tokens</div>
          </div>
          <div className="bg-[#1a1a2e] rounded-lg p-4 border border-amber-900">
            <div className="text-2xl font-bold text-amber-400">${stats.cost.toFixed(2)}</div>
            <div className="text-xs text-gray-400">Total Cost</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <input
            type="text"
            placeholder="Search tasks..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 bg-[#1a1a2e] border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
          />
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="bg-[#1a1a2e] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-500"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="deployed">Deployed</option>
            <option value="qa_failed">QA Failed</option>
            <option value="cleaned">Cleaned</option>
          </select>
        </div>

        {/* Task List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin text-4xl mb-4">⚙️</div>
            <p className="text-gray-400">Loading tasks...</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredTasks.map(task => (
              <div
                key={task.id}
                className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4 hover:border-cyan-700 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: `${statusColors[task.status] || '#666'}20`,
                          color: statusColors[task.status] || '#999',
                          border: `1px solid ${statusColors[task.status] || '#666'}40`,
                        }}
                      >
                        {task.status}
                      </span>
                      {task.qa_passed !== null && (
                        <span className={`text-xs ${task.qa_passed ? 'text-green-400' : 'text-red-400'}`}>
                          {task.qa_passed ? '✓ QA Passed' : '✗ QA Failed'}
                          {task.qa_score && ` (${task.qa_score})`}
                        </span>
                      )}
                      <span className="text-xs text-gray-500 font-mono">{task.task_id.slice(0, 8)}</span>
                    </div>
                    <p className="text-white text-sm line-clamp-2">{task.description || 'No description'}</p>
                    <div className="flex gap-4 mt-2 text-xs text-gray-500">
                      <span>{formatDate(task.completed_at || task.created_at)}</span>
                      {task.duration_ms && <span>⏱ {formatDuration(task.duration_ms)}</span>}
                      {task.tokens_used > 0 && <span>🔤 {task.tokens_used.toLocaleString()} tokens</span>}
                      {task.cost_usd > 0 && <span>💰 ${task.cost_usd.toFixed(4)}</span>}
                      {task.files_generated?.length > 0 && (
                        <span>📁 {task.files_generated.length} files</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {filteredTasks.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No tasks found
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
};

export default TaskHistoryPage;
